import { FormEvent, useEffect, useMemo, useId, useState } from "react";
import { Link } from "react-router-dom";

import { api } from "../../api/client";
import { AlertBanner } from "../../components/feedback/AlertBanner";
import { EarTag } from "../../components/tags/EarTag";
import { AnimalDetailModal } from '../../components/modals/AnimalDetailModal';
import { ResponsiveTable } from '../../components/ui/ResponsiveTable';
import { Field } from '../../components/forms/Field';
import { apiError } from "../../lib/apiError";
import { formatDate, SEX_LABELS } from "../../lib/format";
import { passwordFieldProps } from "../../lib/inputPrivacy";
import { encryptIrzPassword } from "../../lib/irz/irzCrypto";
import { IRZ_PURGE_PHRASE } from "../../lib/deletion";
import { useModalHistoryEntry } from "../../lib/modalHistory";
import type {
  IrzHerdDiff,
  IrzImportResult,
  IrzLocalAnimal,
  IrzSession,
  IrzSyncDetailsResult,
  IrzSyncDocumentsResult,
  Animal,
} from "../../types";

function mapLocalToAnimal(animals: IrzLocalAnimal[]): Animal[] {
  return animals.map((a) => ({
    tag: a.tag,
    species: "sheep",
    sex: a.sex || "female",
    birth_date: a.birth_date,
    arrival_date: null,
    mother_tag: null,
    breed: a.breed,
    program_status: "unspecified",
    status: "active",
  }));
}

export function IrzPage() {
  const [session, setSession] = useState<IrzSession | null>(null);
  const [animals, setAnimals] = useState<IrzLocalAnimal[]>([]);
  const [diff, setDiff] = useState<IrzHerdDiff | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [busyLabel, setBusyLabel] = useState<string | null>(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [detailTag, setDetailTag] = useState<string | null>(null);
  const [conflictTags, setConflictTags] = useState<string[] | null>(null);
  const [replaceConfirmOpen, setReplaceConfirmOpen] = useState(false);
  const [documentsConfirmOpen, setDocumentsConfirmOpen] = useState(false);
  const [documentsRemaining, setDocumentsRemaining] = useState(0);
  const [documentsBatchLimit, setDocumentsBatchLimit] = useState(80);
  const [helpOpen, setHelpOpen] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeMode, setPurgeMode] = useState<"delete_animals" | "strip_fields">(
    "delete_animals",
  );
  const [purgePreviewCount, setPurgePreviewCount] = useState(0);
  const [purgePassword, setPurgePassword] = useState("");
  const [purgePhrase, setPurgePhrase] = useState("");
  const fieldNs = useId().replace(/:/g, "");

  const sheepList = useMemo(() => mapLocalToAnimal(animals), [animals]);

  useModalHistoryEntry(conflictTags != null, () => setConflictTags(null));
  useModalHistoryEntry(replaceConfirmOpen, () => setReplaceConfirmOpen(false));
  useModalHistoryEntry(documentsConfirmOpen, () => setDocumentsConfirmOpen(false));
  useModalHistoryEntry(helpOpen, () => setHelpOpen(false));
  useModalHistoryEntry(purgeOpen, () => setPurgeOpen(false));

  async function loadLocal() {
    const [sessionRes, animalsRes, previewRes] = await Promise.all([
      api.GET("/api/irz/session"),
      api.GET("/api/irz/local-animals"),
      api.GET("/api/data/purge-irz/preview"),
    ]);
    if (sessionRes.error) {
      setError(apiError(sessionRes.error, "Nie udało się sprawdzić sesji IRZ."));
      return;
    }
    if (animalsRes.error) {
      setError(apiError(animalsRes.error, "Nie udało się wczytać lokalnych zwierząt IRZ."));
      return;
    }
    setError(null);
    setSession(sessionRes.data as IrzSession);
    setAnimals((animalsRes.data as IrzLocalAnimal[]) ?? []);
    if (!previewRes.error) {
      const preview = previewRes.data as { irz_animal_count?: number };
      setPurgePreviewCount(preview.irz_animal_count ?? 0);
    }
  }

  useEffect(() => {
    void loadLocal();
  }, []);

  useEffect(() => {
    if (!busy || !busyLabel) {
      return;
    }
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [busy, busyLabel]);

  function applyImportResult(result: IrzImportResult) {
    setError(null);
    setInfo(
      result.message ||
        `Zapisano: +${result.created}, aktualizacja ${result.updated}, pominięte ${result.skipped}.`,
    );
    if (result.conflicts?.length) {
      setConflictTags(result.conflicts);
    } else {
      setConflictTags(null);
    }
  }

  async function onLogin(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setInfo(null);
    try {
      const encrypted = await encryptIrzPassword(password);
      const created = await api.POST("/api/irz/session", {
        body: { username: username.trim(), password_encrypted: encrypted },
      });
      if (created.error) {
        setError(apiError(created.error, "Nie udało się zalogować do IRZplus."));
        return;
      }
      setPassword("");
      setSession(created.data as IrzSession);
      setError(null);
      setInfo("Sesja IRZplus aktywna.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Nie udało się zaszyfrować hasła IRZ.");
    } finally {
      setBusy(false);
    }
  }

  async function fetchList() {
    setBusy(true);
    setInfo(null);
    const fetched = await api.POST("/api/irz/animals", { body: {} });
    setBusy(false);
    if (fetched.error) {
      setError(apiError(fetched.error, "Nie udało się pobrać listy z IRZplus."));
      return;
    }
    const data = fetched.data as { count?: number; message?: string };
    setError(null);
    setInfo(data.message || `Pobrano ${data.count ?? 0} zwierząt z IRZplus (bez zapisu).`);
  }

  async function importMerge(overwriteAllConflicts = false) {
    setBusy(true);
    setInfo(null);
    const imported = await api.POST("/api/irz/import", {
      body: {
        mode: "merge",
        overwrite_all_conflicts: overwriteAllConflicts,
        overwrite_tags: [],
      },
    });
    setBusy(false);
    if (imported.error) {
      setError(apiError(imported.error, "Nie udało się zapisać listy do bazy."));
      return;
    }
    const result = imported.data as IrzImportResult;
    applyImportResult(result);
    await loadLocal();
  }

  async function importReplaceAll() {
    setBusy(true);
    setInfo(null);
    setReplaceConfirmOpen(false);
    const imported = await api.POST("/api/irz/import", {
      body: { mode: "replace_all", overwrite_all_conflicts: false, overwrite_tags: [] },
    });
    setBusy(false);
    if (imported.error) {
      setError(apiError(imported.error, "Nie udało się zastąpić bazy danymi z IRZ."));
      return;
    }
    const result = imported.data as IrzImportResult;
    applyImportResult(result);
    await loadLocal();
  }

  async function syncDetails() {
    setBusy(true);
    setBusyLabel(
      "Synchronizacja w toku — nie zamykaj tej strony i nie przerywaj.",
    );
    setInfo(null);
    const synced = await api.POST("/api/irz/sync-details", {
      body: { only_outdated: true },
    });
    setBusy(false);
    setBusyLabel(null);
    if (synced.error) {
      setError(apiError(synced.error, "Nie udało się zsynchronizować szczegółów IRZ."));
      return;
    }
    const result = synced.data as IrzSyncDetailsResult;
    setError(null);
    setInfo(
      result.message ||
        `Zsynchronizowano szczegóły: ${result.synced} z ${result.requested}.`,
    );
    await loadLocal();
  }

  async function syncDocuments() {
    setDocumentsConfirmOpen(false);
    setBusy(true);
    setBusyLabel(
      "Pobieranie w toku — nie zamykaj tej strony i nie przerywaj. Postęp zapisujemy na bieżąco.",
    );
    setInfo(null);
    setDocumentsRemaining(0);
    const synced = await api.POST("/api/irz/sync-documents", {
      body: { full_history: false },
    });
    setBusy(false);
    setBusyLabel(null);
    if (synced.error) {
      setError(apiError(synced.error, "Nie udało się pobrać danych z dokumentów IRZ."));
      return;
    }
    const result = synced.data as IrzSyncDocumentsResult;
    setError(null);
    setDocumentsRemaining(result.remaining ?? 0);
    setDocumentsBatchLimit(result.batch_limit ?? 80);
    setInfo(
      result.message ||
        `Przetworzono ${result.processed} dokumentów (+${result.animals_created} zwierząt).`,
    );
    await loadLocal();
  }

  async function compareHerd() {
    setBusy(true);
    setInfo(null);
    const compared = await api.POST("/api/irz/herd-diff", { body: {} });
    setBusy(false);
    if (compared.error) {
      setError(apiError(compared.error, "Nie udało się porównać stada z IRZ."));
      return;
    }
    const result = compared.data as IrzHerdDiff;
    setDiff(result);
    setError(null);
    setInfo(result.message || (result.up_to_date ? "Stado jest zgodne z IRZplus." : "Są różnice względem IRZplus."));
  }

  async function logoutIrz() {
    setBusy(true);
    const closed = await api.DELETE("/api/irz/session");
    setBusy(false);
    if (closed.error) {
      setError(apiError(closed.error, "Nie udało się zakończyć sesji IRZ."));
      return;
    }
    setSession(closed.data as IrzSession);
    setDiff(null);
    setInfo("Sesja IRZplus zakończona.");
  }

  async function onPurgeIrz(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setInfo(null);
    const purged = await api.POST("/api/data/purge-irz", {
      body: {
        mode: purgeMode,
        password: purgePassword,
        confirm_phrase: purgePhrase,
      },
    });
    setBusy(false);
    if (purged.error) {
      setError(apiError(purged.error, "Nie udało się usunąć danych IRZ."));
      return;
    }
    const result = purged.data as {
      deleted_animals?: number;
      stripped_animals?: number;
    };
    setPurgeOpen(false);
    setPurgePassword("");
    setPurgePhrase("");
    setError(null);
    if (purgeMode === "delete_animals") {
      setInfo(`Usunięto ${result.deleted_animals ?? 0} zwierząt pobranych z IRZ.`);
    } else {
      setInfo(`Wyczyszczono dane IRZ u ${result.stripped_animals ?? 0} zwierząt.`);
    }
    await loadLocal();
  }

  return (
    <main className="page" data-testid="irz-page">
      <header className="page__header page__header--row">
        <div>
          <p className="page__kicker">IRZplus</p>
          <h1>IRZ</h1>
          <p className="muted">
            {session?.active
              ? `Sesja aktywna${session.producer ? ` · ${session.producer}` : ""}.`
              : "Brak aktywnej sesji — zaloguj się, aby pobrać listę albo porównać stado."}
          </p>
        </div>
        <button
          type="button"
          className="btn btn--ghost"
          data-testid="irz-help-open"
          onClick={() => setHelpOpen(true)}
        >
          Jak działa IRZ
        </button>
      </header>
      <AlertBanner variant="error" message={error} onDismiss={() => setError(null)} />
      <AlertBanner variant="ok" message={info} onDismiss={() => setInfo(null)} />
      {busy && busyLabel ? (
        <p className="banner banner--warn" role="status" data-testid="irz-busy-banner">
          <strong>{busyLabel}</strong>
        </p>
      ) : null}
      {documentsRemaining > 0 && !busy ? (
        <p className="banner banner--info" role="status" data-testid="irz-documents-remaining">
          Zostało jeszcze {documentsRemaining} dokumentów. Kliknij ponownie „Pobierz z
          dokumentów”, aby pobrać kolejną partię (max. {documentsBatchLimit} zgłoszeń na
          raz).
        </p>
      ) : null}

      <p className="guide-callout">
        Po imporcie aktualnego stanu możesz dociągnąć historię ze złożonych dokumentów IRZ
        (urodzenia, padnięcia, uboje, przemieszczenia) — przetwarzane są tylko nowe zgłoszenia,
        partiami po {documentsBatchLimit}, żeby nie przeciążać IRZplus.{" "}
        <Link className="guide-inline-link" to="/guide#planowane">
          Inne planowane rozszerzenia
        </Link>
        .
      </p>

      {!session?.active ? (
        <form className="panel form" onSubmit={(event) => void onLogin(event)}>
          <h2 className="panel__title">Logowanie do IRZplus</h2>
          <p className="muted">
            Hasło jest szyfrowane w przeglądarce przed wysłaniem i nie jest zapisywane.
          </p>
          <Field label="Numer producenta (login)">
            <input
              className="input"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              autoComplete="off"
              data-lpignore="true"
              data-1p-ignore="true"
              name={`${fieldNs}-irz-username`}
              required
            />
          </Field>
          <Field label="Hasło IRZplus">
            <input
              className="input"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              {...passwordFieldProps(`${fieldNs}-irz-password`)}
            />
          </Field>
          <button className="btn" type="submit" disabled={busy}>
            Zaloguj do IRZplus
          </button>
        </form>
      ) : (
        <section className="panel">
          <h2 className="panel__title">Akcje</h2>
          <div className="actions">
            <button className="btn" type="button" disabled={busy} onClick={() => void fetchList()}>
              Pobierz listę
            </button>
            <button className="btn" type="button" disabled={busy} onClick={() => void importMerge()}>
              Zapisz do bazy (scal)
            </button>
            <button
              className="btn btn--ghost"
              type="button"
              disabled={busy}
              onClick={() => setReplaceConfirmOpen(true)}
            >
              Zastąp całą bazę
            </button>
            <button
              className="btn"
              type="button"
              disabled={busy}
              data-testid="irz-sync-details"
              onClick={() => void syncDetails()}
            >
              Synchronizuj szczegóły
            </button>
            <button
              className="btn"
              type="button"
              disabled={busy || animals.length === 0}
              data-testid="irz-sync-documents"
              onClick={() => setDocumentsConfirmOpen(true)}
            >
              Pobierz z dokumentów
            </button>
            <button className="btn" type="button" disabled={busy} onClick={() => void compareHerd()}>
              Porównaj ze stadem
            </button>
            <button className="btn btn--ghost" type="button" disabled={busy} onClick={() => void logoutIrz()}>
              Wyloguj IRZ
            </button>
          </div>
        </section>
      )}

      {diff ? (
        <section className="panel">
          <h2 className="panel__title">Różnice względem IRZplus</h2>
          <div className="finance-cards">
            <div className="finance-card">
              <p className="finance-card__label">IRZ</p>
              <p className="finance-card__value">{diff.irz_count}</p>
            </div>
            <div className="finance-card">
              <p className="finance-card__label">Lokalnie aktywne</p>
              <p className="finance-card__value">{diff.local_active_count}</p>
            </div>
            <div className="finance-card">
              <p className="finance-card__label">Tylko w IRZ</p>
              <p className="finance-card__value">{diff.only_in_irz.length}</p>
            </div>
            <div className="finance-card">
              <p className="finance-card__label">Tylko w aplikacji</p>
              <p className="finance-card__value">{diff.only_in_app.length}</p>
            </div>
            <div className="finance-card">
              <p className="finance-card__label">Zmienione</p>
              <p className="finance-card__value">{diff.changed.length}</p>
            </div>
          </div>
          <ul className="list">
            {diff.only_in_irz.map((item) => (
              <li key={`irz-${item.tag}`} className="list__item">
                <span>
                  Tylko w IRZ: <strong><EarTag value={item.tag} /></strong>
                  {item.summary ? <span className="muted"> — {item.summary}</span> : null}
                </span>
              </li>
            ))}
            {diff.only_in_app.map((item) => (
              <li key={`app-${item.tag}`} className="list__item">
                <span>
                  Tylko w aplikacji: <strong><EarTag value={item.tag} /></strong>
                  {item.summary ? <span className="muted"> — {item.summary}</span> : null}
                </span>
              </li>
            ))}
            {diff.changed.map((item) => (
              <li key={`chg-${item.tag}`} className="list__item">
                <span>
                  Zmiana: <strong><EarTag value={item.tag} /></strong>
                  <span className="muted"> — {item.cause_summary}</span>
                </span>
              </li>
            ))}
            {diff.only_in_irz.length + diff.only_in_app.length + diff.changed.length === 0 ? (
              <li className="muted">Brak różnic na liście.</li>
            ) : null}
          </ul>
        </section>
      ) : null}

      <section className="panel">
        <h2 className="panel__title">Zwierzęta z IRZ w aplikacji</h2>
        <div className="table-wrap">
          <ResponsiveTable className="tbl">
            <thead>
              <tr>
                <th>Kolczyk</th>
                <th>Płeć</th>
                <th>Rasa</th>
                <th>Urodzenie</th>
                <th>Status IRZ</th>
              </tr>
            </thead>
            <tbody>
              {animals.length === 0 ? (
                <tr className="tbl__empty-row">
                  <td colSpan={5} className="muted">
                    Brak lokalnych zwierząt z IRZplus.
                  </td>
                </tr>
              ) : (
                animals.map((animal) => (
                  <tr key={animal.tag}>
                    <td>
                      <button
                        type="button"
                        className="irz-tag-link"
                        data-testid="animal-tag-link"
                        onClick={() => setDetailTag(animal.tag)}
                      >
                        <EarTag value={animal.tag} />
                      </button>
                    </td>
                    <td>{SEX_LABELS[animal.sex] || animal.sex}</td>
                    <td>{animal.breed || "—"}</td>
                    <td>{formatDate(animal.birth_date)}</td>
                    <td>{animal.irz_status || "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </ResponsiveTable>
        </div>
      </section>

      <AnimalDetailModal
        tag={detailTag}
        animals={sheepList}
        onClose={() => setDetailTag(null)}
        onOpenTag={setDetailTag}
      />

      {helpOpen ? (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="irz-help-title"
          data-testid="irz-help-modal"
          onClick={() => setHelpOpen(false)}
        >
          <div className="modal modal--lg" onClick={(event) => event.stopPropagation()}>
            <h3 className="modal__title" id="irz-help-title">
              Jak działa podstrona IRZ
            </h3>
            <p className="modal__text">
              To most między lokalną bazą aplikacji a oficjalnym IRZplus. Bez aktywnej sesji widzisz
              formularz logowania; po zalogowaniu pojawiają się akcje synchronizacji.
            </p>
            <ol className="irz-help-list">
              <li>
                <strong>Logowanie</strong>
                <p className="muted">
                  Numer producenta i hasło IRZplus. Hasło jest szyfrowane w przeglądarce — serwer nie
                  trzyma go w postaci jawnej. Sesja pozwala wołać IRZplus bez ponownego wpisywania
                  hasła.
                </p>
              </li>
              <li>
                <strong>Pobierz listę</strong>
                <p className="muted">
                  Pobiera aktualną listę zwierząt z IRZplus bez zapisu do bazy — podgląd i
                  przygotowanie.
                </p>
              </li>
              <li>
                <strong>Zapisz do bazy (scal)</strong>
                <p className="muted">
                  Importuje listę IRZ do aplikacji: dodaje brakujące, aktualizuje istniejące. Lokalne
                  konflikty (zwierzęta zmienione u Ciebie) mogą zostać pominięte — wtedy możesz je
                  nadpisać w oknie konfliktów.
                </p>
              </li>
              <li>
                <strong>Zastąp całą bazę</strong>
                <p className="muted">
                  Nadpisuje lokalne stado listą z IRZplus (nieodwracalne). Używane, gdy lokalna baza
                  ma być 1:1 z IRZ.
                </p>
              </li>
              <li>
                <strong>Synchronizuj szczegóły</strong>
                <p className="muted">
                  Dla zwierząt już w bazie dociąga z IRZ bogatsze dane (zdarzenia, przemieszczenia,
                  środki identyfikacji), nie tylko samą listę kolczyków.
                </p>
              </li>
              <li>
                <strong>Pobierz z dokumentów</strong>
                <p className="muted">
                  Analizuje złożone przez Ciebie zgłoszenia IRZ (urodzenia, padnięcia, uboje,
                  przemieszczenia) i uzupełnia stado. Dokumenty już przetworzone są pomijane.
                  Przy dużej liczbie zgłoszeń pobieramy je partiami (do {documentsBatchLimit} na
                  raz) — nie zamykaj strony w trakcie; kolejne partie uruchomisz ponownie.
                </p>
              </li>
              <li>
                <strong>Porównaj ze stadem</strong>
                <p className="muted">
                  Pokazuje różnice: tylko w IRZ, tylko w aplikacji, zmienione — bez automatycznego
                  zapisu.
                </p>
              </li>
              <li>
                <strong>Wyloguj IRZ</strong>
                <p className="muted">Kończy sesję IRZplus.</p>
              </li>
            </ol>
            <p className="modal__text modal__text--sm">
              Na dole strony jest tabela lokalnych zwierząt z IRZ. Import obejmuje obecnie owce —
              inne gatunki są pomijane.
            </p>
            <p className="modal__text modal__text--sm">
              Typowy przepływ: zaloguj → pobierz listę → scal (lub zastąp) → synchronizuj szczegóły →
              ewentualnie porównaj ze stadem.
            </p>
            <div className="modal__actions">
              <button
                type="button"
                className="btn"
                data-testid="irz-help-close"
                onClick={() => setHelpOpen(false)}
              >
                Zamknij
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {conflictTags ? (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          data-testid="irz-conflicts-modal"
          onClick={() => setConflictTags(null)}
        >
          <div className="modal" onClick={(event) => event.stopPropagation()}>
            <h3 className="modal__title">Konflikty importu</h3>
            <p className="modal__text">
              Następujące kolczyki mają lokalne zmiany i zostały pominięte:
            </p>
            <ul className="list">
              {conflictTags.map((tag) => (
                <li key={tag} className="list__item">
                  <strong>
                    <EarTag value={tag} />
                  </strong>
                </li>
              ))}
            </ul>
            <div className="modal__actions">
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setConflictTags(null)}
              >
                Anuluj
              </button>
              <button
                type="button"
                className="btn"
                disabled={busy}
                data-testid="irz-overwrite-conflicts"
                onClick={() => {
                  setConflictTags(null);
                  void importMerge(true);
                }}
              >
                Nadpisz wszystkie konflikty
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <section className="panel panel--danger" data-testid="irz-purge-section">
        <h2 className="panel__title">Zarządzanie danymi IRZ</h2>
        <p className="muted">
          Dotyczy {purgePreviewCount} zwierząt z danymi IRZ w aplikacji. Operacja jest
          nieodwracalna.
        </p>
        <button
          type="button"
          className="btn btn--danger"
          data-testid="irz-purge-open"
          disabled={purgePreviewCount === 0 || busy}
          onClick={() => setPurgeOpen(true)}
        >
          Usuń dane IRZ…
        </button>
      </section>

      {purgeOpen ? (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          data-testid="irz-purge-modal"
          onClick={() => setPurgeOpen(false)}
        >
          <div className="modal" onClick={(event) => event.stopPropagation()}>
            <h3 className="modal__title">Usuń dane IRZ</h3>
            <form onSubmit={(event) => void onPurgeIrz(event)}>
              <fieldset className="radio-group">
                <label className="list__check">
                  <input
                    type="radio"
                    name="purge-mode"
                    checked={purgeMode === "delete_animals"}
                    onChange={() => setPurgeMode("delete_animals")}
                  />
                  <span>Usuń całe zwierzęta pobrane z IRZ</span>
                </label>
                <label className="list__check">
                  <input
                    type="radio"
                    name="purge-mode"
                    checked={purgeMode === "strip_fields"}
                    onChange={() => setPurgeMode("strip_fields")}
                  />
                  <span>Usuń tylko dane IRZ, zostaw rekordy zwierząt</span>
                </label>
              </fieldset>
              <Field label={`Fraza potwierdzająca (${IRZ_PURGE_PHRASE})`}>
                <input
                  className="input"
                  required
                  value={purgePhrase}
                  onChange={(event) => setPurgePhrase(event.target.value)}
                  data-testid="irz-purge-phrase"
                />
              </Field>
              <Field label="Hasło administratora">
                <input
                  className="input"
                  type="password"
                  required
                  value={purgePassword}
                  onChange={(event) => setPurgePassword(event.target.value)}
                  data-testid="irz-purge-password"
                  {...passwordFieldProps(`${fieldNs}-irz-purge`)}
                />
              </Field>
              <div className="modal__actions">
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => setPurgeOpen(false)}
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  className="btn btn--danger"
                  disabled={busy}
                  data-testid="irz-purge-confirm"
                >
                  Usuń dane IRZ
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {replaceConfirmOpen ? (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          data-testid="irz-replace-modal"
          onClick={() => setReplaceConfirmOpen(false)}
        >
          <div className="modal" onClick={(event) => event.stopPropagation()}>
            <h3 className="modal__title">Zastąpić całą bazę?</h3>
            <p className="modal__text">
              Lokalne stado zostanie zastąpione listą z IRZplus. Tej operacji nie da się cofnąć.
            </p>
            <div className="modal__actions">
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setReplaceConfirmOpen(false)}
              >
                Anuluj
              </button>
              <button
                type="button"
                className="btn btn--danger"
                disabled={busy}
                data-testid="irz-replace-confirm"
                onClick={() => void importReplaceAll()}
              >
                Zastąp całą bazę
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {documentsConfirmOpen ? (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          data-testid="irz-documents-modal"
          onClick={() => setDocumentsConfirmOpen(false)}
        >
          <div className="modal" onClick={(event) => event.stopPropagation()}>
            <h3 className="modal__title">Pobrać dane z dokumentów IRZ?</h3>
            <p className="modal__text">
              Przetwarzane będą tylko nowe dokumenty od ostatniego pobrania. Na ich podstawie
              aplikacja uzupełni historię stada (m.in. urodzenia, padnięcia, uboje i
              przemieszczenia) bez nadpisywania ręcznie dodanych zwierząt.
            </p>
            <p className="modal__text">
              Pobieranie może potrwać. Nie zamykaj tej strony i nie przerywaj — postęp jest
              zapisywany na bieżąco. Przy dużej liczbie zgłoszeń bierzemy maksymalnie{" "}
              {documentsBatchLimit} dokumentów na raz, żeby nie przeciążać IRZplus; pozostałe
              pobierzesz kolejnym kliknięciem.
            </p>
            <div className="modal__actions">
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setDocumentsConfirmOpen(false)}
              >
                Anuluj
              </button>
              <button
                type="button"
                className="btn"
                disabled={busy}
                data-testid="irz-documents-confirm"
                onClick={() => void syncDocuments()}
              >
                Pobierz z dokumentów
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </main>
  );
}
